import type {
  BuergschaftstemplateDetailsFuerAufgabenListe,
  BürgschaftstemplateAllgemein as BuergschaftstemplateAllgemein,
  Historieneintrag,
  HistorieneintragAGT,
} from '@/types/Dokument';
import { Templatetyp } from '@/types/Dokument';
import type {
  Bürgschaft as Buergschaft,
  Bürgschaftsart as Buergschaftsart,
} from '@/types/Bürgschaft';
import {
  configForDownload,
  configForJsonPost,
  configForMultipartFormData,
  toJsonBlob,
} from '@/connection/base';
import { SharedDokumentService } from '@/shared/api/services/dokument/dokument.ts';
import type {
  Baurecht,
  BürgschaftstemplateBürgentext as BuergschaftstemplateBuergentext,
} from '@/types/DokumentBGT';
import type {
  BürgschaftstemplateÖffentlich as BuergschaftstemplateOeffentlich,
  TemplatetypÖffentlich as TemplatetypOeffentlich,
} from '@/types/DokumentOEF';
import type {
  BürgschaftstemplateAuftraggebertext as BuergschaftstemplateAuftraggebertext,
  Prüfung as Pruefung,
} from '@/types/DokumentAGT';
import type { Dokument } from '@/types/DokumentUpload.ts';

export class DokumentService extends SharedDokumentService {
  async holeBuergschaftstemplateDetailsFuerAufgabenListe(
    templateId: string,
  ): Promise<BuergschaftstemplateDetailsFuerAufgabenListe> {
    return this.httpClient.get(
      `${this.dokumentServiceBaseUrlAg}/buergschaftstemplates/${templateId}/aufgabendetails`,
    );
  }

  async isAbgelehntePruefungVorhanden(buergschaftsart: Buergschaftsart): Promise<boolean> {
    return this.httpClient.get(
      `${this.dokumentServiceBaseUrlAg}/buergschaftstemplates/${buergschaftsart}/aufgaben`,
    );
  }

  async holeTemplateNachIdMitVertragsdatum(
    templateId: string,
    vertragsdatum: string,
  ): Promise<BuergschaftstemplateAllgemein> {
    return this.httpClient.get(
      `${this.dokumentServiceBaseUrlAg}/buergschaftstemplates/${templateId}`,
      { params: { vertragsdatum } },
    );
  }

  async holeTemplateNachId(templateId: string): Promise<BuergschaftstemplateAllgemein> {
    return this.httpClient.get(
      `${this.dokumentServiceBaseUrlAg}/buergschaftstemplates/${templateId}`,
    );
  }

  async holeBuergschaftKopiePdf(buergschaft: Buergschaft): Promise<BlobPart> {
    return this.httpClient.get(
      `${this.dokumentServiceBaseUrlAg}/buergschaftsdokumente/buergschaftskopie_${buergschaft.bürgschaftId}.pdf`,
      { config: configForDownload },
    );
  }

  async holeBuergschaftKopiePdfFuerAuftragnehmer(buergschaft: Buergschaft): Promise<BlobPart> {
    return this.httpClient.get(
      `${this.dokumentServiceBaseUrlAg}/buergschaftsdokumente/buergschaft_fuer_auftraggeber_${buergschaft.bürgschaftId}.pdf`,
      { config: configForDownload },
    );
  }

  async isBuergschaftDokumentKopieVorhanden(buergschaftId: number): Promise<boolean> {
    return this.httpClient.get(
      `${this.dokumentServiceBaseUrlAg}/buergschaftsdokumente/${buergschaftId}/kopie/vorhanden`,
    );
  }

  async isBuergschaftDokumentVorhanden(buergschaftId: number): Promise<boolean> {
    return this.httpClient.get(
      `${this.dokumentServiceBaseUrlAg}/buergschaftsdokumente/${buergschaftId}/vorhanden`,
    );
  }

  async isBuergschaftDokumentFuerAuftraggeberVorhanden(buergschaftId: number): Promise<boolean> {
    return this.httpClient.get(
      `${this.dokumentServiceBaseUrlAg}/buergschaftsdokumente/${buergschaftId}/erweitertvorhanden`,
    );
  }

  async holeTemplatePdf(
    buergschaftstemplateId: string,
    baurecht?: string,
    befristung?: boolean,
  ): Promise<Blob> {
    return this.httpClient.get(
      `${this.dokumentServiceBaseUrlAg}/buergschaftstemplates/buergschaftstemplate_${buergschaftstemplateId}.pdf`,
      {
        params: {
          befristung,
          recht: baurecht,
        },
        config: configForDownload,
      },
    );
  }

  async holebuergschaftstemplateId(
    auftraggeberId: string,
    vertragsdatum?: string,
    buergschaftsart?: string,
    buergeId?: string,
  ): Promise<BuergschaftstemplateAllgemein> {
    return this.httpClient.get(`${this.dokumentServiceBaseUrlAg}/buergschaftstemplate`, {
      params: {
        buergeId,
        auftraggeberId,
        vertragsdatum,
        buergschaftsart,
      },
    });
  }

  async holeBuergentemplatesPerBuerge(
    buergschaftsart: Buergschaftsart,
    buergeId: string,
  ): Promise<BuergschaftstemplateBuergentext[]> {
    return this.httpClient.get(`${this.dokumentServiceBaseUrlAg}/buergschaftstemplates`, {
      params: {
        templatetyp: Templatetyp.BGT,
        buergschaftsart,
        buergeId,
      },
    });
  }

  async holeOeffentlicheTemplates(
    buergschaftsart: Buergschaftsart,
  ): Promise<BuergschaftstemplateOeffentlich[]> {
    return this.httpClient.get(`${this.dokumentServiceBaseUrlAg}/buergschaftstemplates`, {
      params: {
        templatetyp: Templatetyp.OET,
        buergschaftsart,
      },
    });
  }

  async holeBuergentemplates(
    buergschaftsart: Buergschaftsart,
  ): Promise<BuergschaftstemplateBuergentext[]> {
    return this.httpClient.get(`${this.dokumentServiceBaseUrlAg}/buergschaftstemplates`, {
      params: {
        templatetyp: Templatetyp.BGT,
        buergschaftsart,
      },
    });
  }

  async getBuergschaftsartenMitTemplateAnzahl(): Promise<[Buergschaftsart, number]> {
    return this.httpClient.get(
      `${this.dokumentServiceBaseUrlAg}/buergschaftstemplates/buergschaftsarten`,
    );
  }

  async holeTemplatesNachBuergschaftsartGruppiert(
    buergschaftsart: Buergschaftsart,
  ): Promise<[string, BuergschaftstemplateAuftraggebertext[]][]> {
    return this.httpClient.get(`${this.dokumentServiceBaseUrlAg}/buergschaftstemplates/arten`, {
      params: { buergschaftsart },
    });
  }

  async holeTemplateHistorieAGT(id: string | number): Promise<HistorieneintragAGT[]> {
    return this.httpClient.get(
      `${this.dokumentServiceBaseUrlAg}/buergschaftstemplates/${id}/historie`,
    );
  }

  async holeTemplateHistorie(id: string | number): Promise<Historieneintrag[]> {
    return this.httpClient.get(
      `${this.dokumentServiceBaseUrlAg}/buergschaftstemplates/${id}/historie`,
    );
  }

  async holePruefungenZuAbgelehntenTemplate(templateId: string): Promise<Pruefung[]> {
    return this.httpClient.get(
      `${this.dokumentServiceBaseUrlAg}/buergschaftstemplates/ablehnungen`,
      {
        params: { buergschaftstemplateId: templateId },
      },
    );
  }

  async archiviereBuergschaftstemplate(templateId: string, gueltigAb: string): Promise<void> {
    return this.httpClient.post(
      `${this.dokumentServiceBaseUrlAg}/buergschaftstemplates/${templateId}/archivieren/${gueltigAb}`,
    );
  }

  async aktiviereOeffentlichenText(
    gueltigAb: string,
    templatetypOeffentlich: TemplatetypOeffentlich,
    buergschaftsart: Buergschaftsart,
  ): Promise<BuergschaftstemplateAllgemein> {
    return this.httpClient.post(
      `${this.dokumentServiceBaseUrlAg}/buergschaftstemplates/oeffentlichertext/aktivierung`,
      {
        data: {
          gültigAb: gueltigAb,
          templatetypÖffentlich: templatetypOeffentlich,
          bürgschaftsart: buergschaftsart,
        },
      },
    );
  }

  async aktiviereBuergentext(
    gueltigAb: string,
    befristung: boolean,
    baurecht: Baurecht,
    buergschaftsart: Buergschaftsart,
  ): Promise<BuergschaftstemplateAllgemein> {
    return this.httpClient.post(
      `${this.dokumentServiceBaseUrlAg}/buergschaftstemplates/buergentexte/aktivierung`,
      {
        data: {
          gültigAb: gueltigAb,
          befristung,
          baurecht,
          bürgschaftsart: buergschaftsart,
        },
      },
    );
  }

  async istTextSchonVorhanden(
    gueltigAb: string,
    buergschaftsart: Buergschaftsart,
  ): Promise<boolean> {
    return this.httpClient.get(
      `${this.dokumentServiceBaseUrlAg}/buergschaftstemplates/validierung`,
      {
        params: { gueltigAb, buergschaftsart },
      },
    );
  }

  async istNeueTextversionHochladen(
    gueltigAb: string,
    buergschaftsart: Buergschaftsart,
    buergschaftstemplateId: string,
  ): Promise<boolean> {
    return this.httpClient.get(
      `${this.dokumentServiceBaseUrlAg}/buergschaftstemplates/validierung/textversion`,
      { params: { gueltigAb, buergschaftsart, buergschaftstemplateId } },
    );
  }

  async istTextAktivierbar(gueltigAb: string, buergschaftsart: Buergschaftsart): Promise<boolean> {
    return this.httpClient.get(
      `${this.dokumentServiceBaseUrlAg}/buergschaftstemplates/aktivierung`,
      {
        params: { gueltigAb, buergschaftsart },
      },
    );
  }

  async holeGescannteDatei(handle: string): Promise<Blob> {
    return this.httpClient.get(
      `${this.dokumentServiceBaseUrlAg}/buergschaftsvorlagen/upload/${handle}`,
      { config: configForDownload },
    );
  }

  async holeVorlagePdf(dmsVorlageId: string, pdf: boolean): Promise<Blob> {
    return this.httpClient.get(
      `${this.dokumentServiceBaseUrlAg}/buergschaftsvorlagen/${dmsVorlageId}/vorlage`,
      { params: { pdf }, config: configForDownload },
    );
  }

  async holeAuftragsvorlage(id: number): Promise<Blob> {
    return this.httpClient.get(
      `${this.dokumentServiceBaseUrlAg}/buergschaftsauftrag/${id}/vorlage`,
      {
        config: configForDownload,
      },
    );
  }

  async erstelleBuergschaftsvorlage(vorlage: FormData) {
    return this.httpClient.post(`${this.dokumentServiceBaseUrlAg}/buergschaftsvorlagen`, {
      data: vorlage,
      config: configForMultipartFormData,
    });
  }

  async manuellerUploadBuergschaftsdokument(
    dokument: Dokument,
    auftraggeberId: string,
    auftragnehmerId: string,
    buergeId: string,
    buergschaftId: number,
    buergschaftIdBuerge: string,
    token?: string,
  ): Promise<void> {
    const formData = new FormData();

    const buergschaftsdaten = {
      auftraggeberId,
      auftragnehmerId,
      buergeId,
      bürgschaftId: buergschaftId,
      bürgschaftIdBürge: buergschaftIdBuerge,
      token,
      dokument,
    };

    formData.append('buergschaftsdaten', toJsonBlob(buergschaftsdaten));

    return this.httpClient.post(`${this.dokumentServiceBaseUrlAg}/buergschaftsdokument/upload`, {
      data: formData,
      config: configForMultipartFormData,
    });
  }

  async loescheBuergschaftsdokument(buergschaftId: string): Promise<void> {
    return this.httpClient.delete(
      `${this.dokumentServiceBaseUrlAg}/buergschaften/${buergschaftId}/dokumente`,
      { config: configForJsonPost },
    );
  }
}
